import { useState, useRef, useEffect } from 'react';
import type { FormEvent, MouseEvent as ReactMouseEvent } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider, CssBaseline, Typography, Button, Dialog, DialogTitle, DialogContent, FormControl, TextField, Stack, Link as MuiLink, Box } from '@mui/material';
import theme from './theme/theme';
import InstagramIcon from '@mui/icons-material/Instagram';
import Dashboard from './pages/Dashboard';
import StylistProfileSettings from '@components/profile/StylistProfileSettings';
import StylistProfileNew from '@components/profile/StylistProfileNew';
import ReservationsPage from './pages/ReservationsPage';
import ReservationsPageV2 from './pages/ReservationsPageV2';
import OnboardingPage from './pages/OnboardingPage';
import StylistPage from './pages/StylistPage';
import CreditLogPage from './pages/CreditLogPage';
import PayoutLogPage from './pages/PayoutLogPage';
import StylistSignupPage from './pages/StylistSignupPage';
import BusinessProfile from './pages/BusinessProfile';
import PlanPage from './pages/PlanPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { supabase } from './services/supabase';
import { formatPhoneWithCountryCode } from '@utils/phoneUtils';
import Layout from './components/Layout';
import { login } from './services/api';
import { initInstagramOAuth } from './services/instagramShowcaseService';
import { isMobileDevice, openOAuthPopup, monitorPopup } from './utils/mobileOAuthHandler';
import './App.scss';
import ShowcaseManagementPage from './pages/ShowcaseManagementPage';
import TransactionPage from './pages/TransactionPage';
import ChatList from './components/chatbox/ChatList';
import ConversationPage from './components/chatbox/ConversationPage';
import MonthlyReport from './pages/MonthlyReport';
import TNC from './pages/TNC';
import PrivacyPolicy from './pages/PrivacyPolicy';
import QuickOnboarding from './components/QuickOnboarding';
import InstagramShowcaseFlow from './components/InstagramShowcaseFlow';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ScrollToTop from './components/ScrollToTop';
import GlobalNotifications from './components/GlobalNotifications/GlobalNotifications';
import { CountProvider } from './context/CountContext';
import { GodModeProvider } from './context/GodModeContext';
import GodModePanel from './components/admin/GodModePanel';
import ErrorBoundary from './components/ErrorBoundary';
import ErrorFallback from './components/ErrorFallback';
import InstagramAuthSuccess from './components/showcase/InstagramAuthSuccess';
import ReviewsPage from './pages/ReviewsPage';
import InstagramDataDeletionPage from './pages/InstagramDataDeletionPage';
import OAuthRedirectPage from './pages/OAuthRedirectPage';
import OAuthCallbackHandler from './pages/OAuthCallbackHandler';
import { updateLanguageFromBackend } from './i18n';
import EmailVerificationSuccess from './pages/EmailVerificationSuccess';
import QuickEmailVerificationSuccess from './pages/QuickEmailVerificationSuccess';
import LanguageDropdown from './components/LanguageDropdown';
import i18n from './i18n';
import { useTranslation } from 'react-i18next';
import PlatformGrowth from './components/PlatformGrowth';
import FullScreenLoadingOverlay from './components/FullScreenLoadingOverlay';
// --- Revamped (new design) pages + classic-version wrapper ---
import ClassicWrapper from './components/ClassicWrapper';
import DashboardNew from './pages/DashboardNew';
import StylistPageNew from './pages/StylistPageNew';
import BusinessProfileNew from './pages/BusinessProfileNew';
import PlanPageNew from './pages/PlanPageNew';
import ShowcaseManagementPageNew from './pages/ShowcaseManagementPageNew';
import ReservationsPageNew from './pages/ReservationsPageNew';
import CreditLogPageNew from './pages/CreditLogPageNew';
import MonthlyReportNew from './pages/MonthlyReportNew';
import ReviewsPageNew from './pages/ReviewsPageNew';
import ChatListNew from './components/chatbox/ChatListNew';
import NotificationPageNew from './components/chatbox/NotificationPageNew';
const queryClient = new QueryClient();

function HomePage(): JSX.Element {
  const { t } = useTranslation();
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string>('');
  const [isForgotPassword, setIsForgotPassword] = useState<boolean>(false);
  const [resetEmailSent, setResetEmailSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnectingInstagram, setIsConnectingInstagram] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);

  useEffect(() => {
    const intendedPath = sessionStorage.getItem('intendedPath');
    if (intendedPath || location.pathname === '/login') {
      setShowLogin(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const floatingElements = document.querySelectorAll<HTMLElement>('.floating');
    floatingElements.forEach(element => {
      const randomDelay = Math.random() * 3; // Random delay between 0 and 3 seconds
      element.style.animationDelay = `${randomDelay}s`;
    });
  }, []);

  useEffect(() => {
    // Load language from localStorage on mount
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Handle Instagram OAuth callback redirect
  useEffect(() => {
    const handleOAuthCallback = async (): Promise<void> => {
      const searchParams = new URLSearchParams(location.search);
      const instagramToken = searchParams.get('instagram_token');

      if (!instagramToken) {
        return; // No token, nothing to do
      }

      console.log('Instagram OAuth successful, checking user status...');

      try {
        // Store token for API calls
        localStorage.setItem('instagram_onboarding_token', instagramToken);

        // Close the connecting overlay
        setIsConnectingInstagram(false);

        // Call backend to check user status and get redirect instruction
        const { verifyInstagramConnection } = await import('./services/instagramShowcaseService');
        const connectionData = await verifyInstagramConnection(instagramToken);

        // Backend tells us where to go
        const redirectTo: string = connectionData.redirectTo || '/instagram-onboarding';  // Fallback to onboarding

        console.log('Backend redirect instruction:', redirectTo);

        // If redirecting to dashboard, auto-login first
        if (redirectTo === '/dashboard' && connectionData.canSkipClaimStep) {
          console.log('Auto-login for returning user...');

          const { autoLoginClaimedAccount } = await import('./services/instagramShowcaseService');
          const loginData = await autoLoginClaimedAccount(instagramToken);

          if (loginData.success && loginData.token) {
            // Store permanent token and user data
            localStorage.setItem('token', loginData.token);
            localStorage.setItem('user', JSON.stringify(loginData.user));

            console.log('Auto-login successful, redirecting to dashboard');

            // Show welcome message
            toast.success(t('instagramShowcase.signIn.welcomeBack', 'Welcome back! Signed in with Instagram.'));

            // Navigate to dashboard
            navigate('/dashboard', {
              replace: true,
              state: {
                message: t('instagramShowcase.signIn.welcome', 'Welcome back!'),
                fromInstagramSignIn: true
              }
            });

            return; // Exit early - done
          }
        }

        // Otherwise, navigate to the backend-provided redirect (or fallback to onboarding)
        navigate(redirectTo, { replace: true });

      } catch (err: unknown) {
        console.error('Error during OAuth callback:', err);
        toast.error(t('instagramShowcase.errors.oauthCallback', 'Failed to complete sign-in. Please try again.'));

        // Fallback to onboarding on error
        navigate('/instagram-onboarding', { replace: true });
      }
    };

    handleOAuthCallback();
  }, [location.search, navigate, t, setIsConnectingInstagram]);

  const toggleLogin = (): void => {
    setShowLogin(!showLogin);
  };

  const handleCTA = (): void => {
    navigate('/onboarding');
  };

  const handleQuickOnboarding = (): void => {
    navigate('/quick-onboarding');
  };

  const handleInstagramConnect = async (): Promise<void> => {
    try {
      console.log('Initiating Instagram OAuth from home page...');

      // Show the connecting overlay
      setIsConnectingInstagram(true);

      // For mobile popup flow, redirect to callback handler page
      const returnUrl: string = isMobileDevice()
        ? `${window.location.origin}/oauth-callback`
        : window.location.origin;

      // Initiate Instagram OAuth with return URL
      const response = await initInstagramOAuth(returnUrl);

      if (!response.authUrl) {
        throw new Error('No auth URL received from backend');
      }

      // Mobile browser: Use popup to prevent Instagram app intercept
      if (isMobileDevice()) {
        console.log('Mobile browser detected - using popup approach');

        // CRITICAL: Open popup BEFORE any async operations (iOS requirement)
        const popup = openOAuthPopup(response.authUrl);

        // Check if popup was blocked
        if (!popup || popup.closed) {
          console.warn('Popup was blocked by browser');
          setIsConnectingInstagram(false);

          // Fallback: Navigate to intermediate page
          navigate('/oauth-redirect', {
            state: {
              authUrl: response.authUrl,
              returnPath: '/'
            }
          });
          return;
        }

        console.log('OAuth popup opened successfully');

        // Monitor popup for completion
        monitorPopup(
          popup,
          async (token: string) => {
            // Success: Store token and check backend status
            console.log('OAuth completed successfully, token received');
            localStorage.setItem('instagram_onboarding_token', token);

            try {
              // Check backend for redirect instruction
              const { verifyInstagramConnection } = await import('./services/instagramShowcaseService');
              const connectionData = await verifyInstagramConnection(token);

              const redirectTo: string = connectionData.redirectTo || '/instagram-onboarding';
              console.log('Backend redirect instruction (mobile):', redirectTo);

              // If dashboard redirect, auto-login first
              if (redirectTo === '/dashboard' && connectionData.canSkipClaimStep) {
                console.log('Auto-login for returning user (mobile)...');

                const { autoLoginClaimedAccount } = await import('./services/instagramShowcaseService');
                const loginData = await autoLoginClaimedAccount(token);

                if (loginData.success && loginData.token) {
                  localStorage.setItem('token', loginData.token);
                  localStorage.setItem('user', JSON.stringify(loginData.user));
                  toast.success(t('instagramShowcase.signIn.welcomeBack', 'Welcome back! Signed in with Instagram.'));
                  setIsConnectingInstagram(false);
                  navigate('/dashboard', { replace: true, state: { fromInstagramSignIn: true } });
                  return;
                }
              }

              // Otherwise navigate to backend's redirect instruction
              setIsConnectingInstagram(false);
              navigate(redirectTo, { replace: true });

            } catch (err: unknown) {
              console.error('Error checking backend status (mobile):', err);
              setIsConnectingInstagram(false);
              navigate('/instagram-onboarding', { replace: true });
            }
          },
          (popupError: Error) => {
            // Error: Show message
            console.error('OAuth popup error:', popupError);
            setIsConnectingInstagram(false);
            toast.error(t('instagramShowcase.connect.error', 'Failed to connect Instagram. Please try again.'));
          }
        );

      } else {
        // Desktop browser: Use traditional redirect (works perfectly)
        console.log('Desktop browser detected - using traditional redirect');
        window.location.href = response.authUrl;
      }

    } catch (err: unknown) {
      console.error('Error initiating Instagram OAuth:', err);
      setIsConnectingInstagram(false);
      toast.error(t('instagramShowcase.connect.error', 'Failed to connect Instagram. Please try again.'));
    }
  };

  const handleClickOutside = (event: globalThis.MouseEvent): void => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setShowLogin(false);
    }
  };

  useEffect(() => {
    if (showLogin) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLogin]);

  const handleLogin = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    try {
      // Sign in with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email: username,
        password: password
      });

      if (error) throw error;

      const user = data.user;

      if (!user.email_confirmed_at && import.meta.env.VITE_NODE_ENV !== "development") {
        setError('Please verify your email before logging in.');
        setErrorCode('email-not-verified');
        return;
      }

      // Get the session for the token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      try {
        // Login with our backend
        const response = await login({
          token: session!.access_token,
          email: user.email!
        });

        console.log('Login successful:', response.data);
        if ((response.data.user.businessName && response.data.user.status !== "pending") || response.data.user.role === "stylist") {
          // Clear any stale God Mode session state before new login
          sessionStorage.removeItem('impersonation_token');
          sessionStorage.removeItem('impersonated_user');
          sessionStorage.removeItem('godmode_original_user');
          sessionStorage.removeItem('godmode_original_token');

          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));

          // Sync phone to Supabase, but never let a malformed phone (non-E.164) block login.
          try {
            const formattedPhone = formatPhoneWithCountryCode(response.data.user.phone ?? '');
            if (formattedPhone) {
              await supabase.auth.updateUser({ phone: formattedPhone });
            }
          } catch (phoneSyncError) {
            console.warn('Skipped Supabase phone sync (invalid format):', (phoneSyncError as Error)?.message);
          }

          const intendedPath = sessionStorage.getItem('intendedPath') || '/dashboard';
          sessionStorage.removeItem('intendedPath');
          updateLanguageFromBackend();
          navigate(intendedPath);
        } else {
          setError('Please wait for approval.');
        }
      } catch (loginError: unknown) {
        // If login to our backend fails, we should sign out from Supabase
        // to maintain consistency
        // Clear God Mode session state to prevent stale impersonation
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonated_user');
        sessionStorage.removeItem('godmode_original_user');
        sessionStorage.removeItem('godmode_original_token');
        await supabase.auth.signOut();
        throw loginError;
      }
    } catch (err: unknown) {
      setError('Login failed. Please check your username and password.');
      console.error('Error logging in:', err);
    }
  };

  const handleResendVerification = async (): Promise<void> => {
    try {
      // For Supabase, we can send a new signup OTP
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: username
      });

      if (error) throw error;

      setError('Verification email sent. Please check your inbox.');
      setErrorCode('');
    } catch (err: unknown) {
      setError('Failed to send verification email.');
      console.error('Error resending verification:', err);
    }
  };

  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    if (!username) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(username, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast.success('Password reset email sent. Please check your inbox.');
    } catch (err: unknown) {
      console.error('Error sending reset email:', err);
      setError('Failed to send reset email. Please check your email address.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = async (lng: string): Promise<void> => {
    await i18n.changeLanguage(lng);
    setCurrentLanguage(lng);
    localStorage.setItem('selectedLanguage', lng); // Save to localStorage
  };

  return (
    <div className="App">
      {/* Floating Language Dropdown */}
      <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, pointerEvents: 'auto' }}>
        <LanguageDropdown currentLanguage={currentLanguage} changeLanguage={handleLanguageChange} />
      </Box>
      {showLogin && (
        <Dialog open={showLogin} onClose={toggleLogin}>
          <DialogTitle>{isForgotPassword ? t('login.resetPassword') : t('login.login')}</DialogTitle>
          <DialogContent>
            {!resetEmailSent ? (
              <form onSubmit={isForgotPassword ? handleForgotPassword : handleLogin}>
                <Stack spacing={3} sx={{ padding: 1, width: {xs: '250px', md: '400px'}}}>
                  <FormControl fullWidth margin="normal">
                    <TextField
                      label={t('login.username')}
                      InputLabelProps={{ id: 'username-label' }}
                      type="email"
                      id="username"
                      name="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="username"
                      disabled={isLoading}
                    />
                  </FormControl>
                  {!isForgotPassword && (
                    <FormControl fullWidth margin="normal">
                      <TextField
                        label={t('login.password')}
                        type="password"
                        id="password"
                        name="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        disabled={isLoading}
                      />
                    </FormControl>
                  )}
                  {error && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {error}
                    </Typography>
                  )}
                  {errorCode === 'email-not-verified' && (
                    <Button
                      onClick={handleResendVerification}
                      sx={{ mt: 1 }}
                      disabled={isLoading}
                    >
                      {t('login.resendVerification')}
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={isLoading}
                  >
                    {isLoading ? t('login.loading') : (isForgotPassword ? t('login.sendResetEmail') : t('login.login'))}
                  </Button>
                  <Button
                    onClick={() => setIsForgotPassword(!isForgotPassword)}
                    color="primary"
                    sx={{ textTransform: 'none' }}
                    disabled={isLoading}
                  >
                    {isForgotPassword ? t('login.backToLogin') : t('login.forgotPassword')}
                  </Button>
                </Stack>
              </form>
            ) : (
              <Box sx={{ padding: 2, textAlign: 'center' }}>
                <Typography variant="body1" gutterBottom>
                  {t('login.resetEmailSent')}
                </Typography>
                <Button
                  onClick={() => {
                    setResetEmailSent(false);
                    setIsForgotPassword(false);
                  }}
                  color="primary"
                  sx={{ mt: 2 }}
                >
                  {t('login.backToLogin')}
                </Button>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}

      <section className="hero">
        {/* <h1>...</h1> */}
        <div className="hero-block">
          <Box sx={{width: {xs: '100%', md: '50%'}}}>
            <img src="https://r2-internal.hkstylist.com/logo.png" style={{width: '100%'}} />
          </Box>
          <h1>{t('home.heroTitle')}</h1>
          <Box sx={{width: {xs: '100%', md: '85%'}}}>
            <div className="hero-description">{t('home.heroDescription')}</div>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: '6px', sm: '10px' },
              mt: 'clamp(15px, 3vw, 20px)',
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {/* <a onClick={handleCTA} className="cta-button">{t('home.registerNow')}</a> */}
            <a
              onClick={handleInstagramConnect}
              className="cta-button"
              style={{
                background: '#E4405F',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                textAlign: 'center'
              }}
              onMouseOver={(e: ReactMouseEvent<HTMLAnchorElement>) => e.currentTarget.style.background = '#C13584'}
              onMouseOut={(e: ReactMouseEvent<HTMLAnchorElement>) => e.currentTarget.style.background = '#E4405F'}
            >
              <InstagramIcon />
              {t('home.instagramOnboarding', 'Instagram Onboarding')}
            </a>
            <a onClick={handleQuickOnboarding} className="cta-button">{t('home.quickRegister', 'Quick Register')}</a>
            <a onClick={toggleLogin} className="cta-button">{t('home.merchantLogin')}</a>
          </Box>
        </div>
      </section>
      <PlatformGrowth />
      <section className="services">
        <h2>{t('home.whyUse')}</h2>
        <div className="services-grid">
            <div className="service-card">
                <svg className="service-icon" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
                    <path d="M12 20v-6M6 20V10M18 20V4"/>
                </svg>
                <h3>{t('home.service1Title')}</h3>
                <p>{t('home.service1Desc')}</p>
            </div>
            <div className="service-card">
                <svg className="service-icon" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
                    <path d="M20 7h-3m-3 0H4m19 5h-9m-3 0H4m19 5h-6m-3 0H4"/>
                </svg>
                <h3>{t('home.service2Title')}</h3>
                <p>{t('home.service2Desc')}</p>
            </div>
            <div className="service-card">
                <svg className="service-icon" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
                <h3>{t('home.service3Title')}</h3>
                <p>{t('home.service3Desc')}</p>
            </div>
        </div>
      </section>
      <section className="reservation-calendar">
        <div className="reservation-title">
          <h3>{t('home.reservationFeatureTitle')}</h3>
        </div>
        <div className="feature-grid">
          <div className="feature-card floating">
            <div className="video-container">
            <img src="https://r2-internal.hkstylist.com/demo.gif" style={{width: '100%'}} />
            </div>
          </div>

          <div className="feature-card floating">
              <svg className="feature-icon" viewBox="0 0 24 24">
              <path fill="#0077cc" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6l5.25 3.15-.75 1.23L11 13V7z"/>
              </svg>
              <div className="feature-title">{t('home.featureReduceCommunicationTitle')}</div>
              <div className="feature-text">{t('home.featureReduceCommunicationDesc')}</div>
          </div>

          <div className="feature-card floating">
              <svg className="feature-icon" viewBox="0 0 24 24">
                  <path fill="#0077cc" d="M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V8H19V19Z"/>
              </svg>
              <div className="feature-title">{t('home.featureReminderTitle')}</div>
              <div className="feature-text">{t('home.featureReminderDesc')}</div>
          </div>

          <div className="feature-card floating">
              <svg className="feature-icon" viewBox="0 0 24 24">
                  <path fill="#0077cc" d="M11.8,10.9c-2.27-0.59-3-1.2-3-2.15c0-1.09,1.01-1.85,2.7-1.85c1.78,0,2.44,0.85,2.5,2.1h2.21 c-0.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94,0.42-3.5,1.68-3.5,3.61c0,2.31,1.91,3.46,4.7,4.13c2.5,0.6,3,1.48,3,2.41 c0,0.69-0.49,1.79-2.7,1.79c-2.06,0-2.87-0.92-2.98-2.1h-2.2c0.12,2.19,1.76,3.42,3.68,3.83V21h3v-2.15 c1.95-0.37,3.5-1.5,3.5-3.55C16.5,12.46,14.07,11.49,11.8,10.9z"/>
              </svg>
              <div className="feature-title">{t('home.featureLowCostTitle')}</div>
              <div className="feature-text">{t('home.featureLowCostDesc')}</div>
          </div>

          <div className="feature-card floating">
              <svg className="feature-icon" viewBox="0 0 24 24">
                  <path fill="#0077cc" d="M21,18V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V18H21M6,4H8V13H6V4M10,4H12V13H10V4M14,4H16V13H14V4M18,4H20V13H18V4M3,16V15A2,2 0 0,1 5,13H19A2,2 0 0,1 21,15V16H3Z"/>
              </svg>
              <div className="feature-title">{t('home.featureCreditsPlanTitle')}</div>
              <div className="feature-text">{t('home.featureCreditsPlanDesc')}</div>
          </div>

          <div className="feature-card floating">
              <svg className="feature-icon" viewBox="0 0 24 24">
                  <path fill="#0077cc" d="M20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4C12.76,4 13.5,4.11 14.2,4.31L15.77,2.74C14.61,2.26 13.34,2 12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12M7.91,10.08L6.5,11.5L11,16L21,6L19.59,4.58L11,13.17L7.91,10.08Z"/>
              </svg>
              <div className="feature-title">{t('home.featureEarlyBirdTitle')}</div>
              <div className="feature-text">{t('home.featureEarlyBirdDesc')}</div>
          </div>

          <div className="feature-card floating">
            <svg className="feature-icon" viewBox="0 0 24 24">
                <path fill="#0077cc" d="M21,11.5A8.5,8.5 0 0,1 12.5,20A8.5,8.5 0 0,1 4,11.5A8.5,8.5 0 0,1 12.5,3A8.5,8.5 0 0,1 21,11.5M12.5,1A10.5,10.5 0 0,0 2,11.5A10.5,10.5 0 0,0 12.5,22A10.5,10.5 0 0,0 23,11.5A10.5,10.5 0 0,0 12.5,1M12,6A1,1 0 0,1 13,7A1,1 0 0,1 12,8A1,1 0 0,1 11,7A1,1 0 0,1 12,6M12,15A1,1 0 0,1 11,14V9A1,1 0 0,1 12,8A1,1 0 0,1 13,9V14A1,1 0 0,1 12,15Z"/>
            </svg>
            <div className="feature-title">{t('home.featureInstantReplyTitle')}</div>
            <div className="feature-text">{t('home.featureInstantReplyDesc')}</div>
        </div>

        <div className="feature-card floating">
            <svg className="feature-icon" viewBox="0 0 24 24">
                <path fill="#0077cc" d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z"/>
            </svg>
            <div className="feature-title">{t('home.featureMultiDeviceTitle')}</div>
            <div className="feature-text">{t('home.featureMultiDeviceDesc')}</div>
        </div>
        <div className="feature-card floating">
              <svg className="feature-icon" viewBox="0 0 24 24">
                  <path fill="#0077cc" d="M3 3h18v2H3V3zm0 4h18v14H3V7zm2 2v10h14V9H5zm7 1h2v2h-2v-2zm-4 0h2v2H8v-2zm8 0h2v2h-2v-2zm-4 4h2v2h-2v-2zm-4 0h2v2H8v-2zm8 0h2v2h-2V2z"/>
                  <path fill="#0077cc" d="M12 2v2h-2V2h2zm0 0h2v2h-2V2z"/>
              </svg>
              <div className="feature-title">{t('home.featureCalendarSyncTitle')}</div>
              <div className="feature-text">{t('home.featureCalendarSyncDesc')}</div>
          </div>
          <div className="feature-card floating">
              <svg className="feature-icon" viewBox="0 0 24 24">
                  <path fill="#0077cc" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                  <path fill="#0077cc" d="M12 6a6 6 0 1 0 6 6 6 6 0 0 0-6-6zm0 10a4 4 0 1 1 4-4 4 4 0 0 1-4 4z"/>
                  <path fill="#0077cc" d="M15 9h2v2h-2z"/>
              </svg>
              <div className="feature-title">{t('home.featureInstagramSyncTitle')}</div>
              <div className="feature-text">{t('home.featureInstagramSyncDesc')}</div>
          </div>
      </div>
      </section>

      <section className="future-features">
        <h2 className="future-title">{t('home.futureFeaturesMainTitle')}</h2>
        <h2 className="future-title">{t('home.futureFeaturesComingTitle')}</h2>
        <div className="feature-grid">
          <div className="feature-card floating">
              <svg className="feature-icon" viewBox="0 0 24 24">
                  <path fill="#0077cc" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                  <path fill="#0077cc" d="M12 4a8 8 0 0 0-8 8h2a6 6 0 0 1 12 0h2a8 8 0 0 0-8-8z"/>
                  <path fill="#0077cc" d="M12 14a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2z"/>
              </svg>
              <div className="feature-title">{t('home.futureFeatureOldClientReminderTitle')}</div>
              <div className="feature-text">{t('home.futureFeatureOldClientReminderDesc')}</div>
          </div>
          <div className="feature-card floating">
              <svg className="feature-icon" viewBox="0 0 24 24">
                <path fill="#0077cc" d="M21 7H3V5h18v2zm0 2H3v10h18V9zm-2 8H5v-6h14v6zm-7-3.5l-1.5-1.5 1.5-1.5 1.5 1.5-1.5 1.5zm-3 0l-1.5-1.5 1.5-1.5 1.5 1.5-1.5 1.5zm6 0l-1.5-1.5 1.5-1.5 1.5 1.5-1.5 1.5z"/>
              </svg>
              <div className="feature-title">{t('home.futureFeatureStylistCouponTitle')}</div>
              <div className="feature-text">{t('home.futureFeatureStylistCouponDesc')}</div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <h3 className="feature-title">{t('home.futureFeatureHaveIdeaTitle')}</h3>
            <p className="feature-text">{t('home.futureFeatureTalkToUs')}</p>
            <a href="mailto:admin@hkstylist.com" className="cta-button">admin@hkstylist.com</a>
        </div>
      </section>

      <div className="process-card" style={{width: '90%', maxWidth: '1200px', margin: '30px auto'}}>
          <h2>{t('home.processMainTitle')}</h2>
          <div className="process-steps">
              <div className="step">
                  <div className="step-number">1</div>
                  <h3>{t('home.processStep1Title')}</h3>
                  <p>{t('home.processStep1Desc')}</p>
              </div>
              <div className="step">
                  <div className="step-number">2</div>
                  <h3>{t('home.processStep2Title')}</h3>
                  <p>{t('home.processStep2Desc')}</p>
              </div>
              <div className="step">
                  <div className="step-number">3</div>
                  <h3>{t('home.processStep3Title')}</h3>
                  <p>{t('home.processStep3Desc')}</p>
              </div>
          </div>
      </div>
      <div className="signup-button-container">
          <a onClick={handleCTA} className="signup-button">{t('home.registerNow')}</a>
      </div>
      {/* Instagram icon and link */}
      <div style={{ textAlign: 'center', margin: '28px 0' }}>
        <div style={{ fontWeight: 500, fontSize: '1.1rem', marginBottom: 18 }}>{t('home.followUsOn')}</div>
        <a href="https://www.instagram.com/hkstylist.biz" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#E4405F', fontWeight: 500, fontSize: '1.3rem', marginBottom: 8 }}>
          {/* Instagram SVG icon */}
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 10 }}>
            <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5A4.25 4.25 0 0 0 20.5 16.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5zm4.25 3.25a5.25 5.25 0 1 1 0 10.5a5.25 5.25 0 0 1 0-10.5zm0 1.5a3.75 3.75 0 1 0 0 7.5a3.75 3.75 0 0 0 0-7.5zm5.13 1.12a1.13 1.13 0 1 1-2.25 0a1.13 1.13 0 0 1 2.25 0z" fill="#E4405F"/>
          </svg>
          @hkstylist.biz
        </a>
        <br />
        <a href="https://www.facebook.com/people/HKStylist-Salon-Tools/61575905103496/" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#1877F3', fontWeight: 500, fontSize: '1.1rem', marginTop: 12 }}>
          {/* Facebook SVG icon */}
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 10 }}>
            <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0" fill="#1877F3"/>
          </svg>
          @HKStylist Salon
        </a>
      </div>
      <footer>
        <p>&copy; 2024 HKStylist Limited. All rights reserved.</p>
        <MuiLink href="/privacy-policy" className="privacy-policy-link" underline="hover">
          {t('home.privacyPolicy')}
        </MuiLink>
      </footer>

      {/* Full Screen Loading Overlay for Instagram Connection */}
      <FullScreenLoadingOverlay
        isOpen={isConnectingInstagram}
        message={t('instagramShowcase.connecting.title', 'Connecting to Instagram...')}
        subMessage={t('instagramShowcase.connecting.description', 'Please wait while we redirect you to Instagram for authorization.')}
        showInstagramIcon={true}
      />
    </div>
  );
}

function App(): JSX.Element {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CountProvider>
            <GodModeProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}
              >
                <ScrollToTop />
                <ToastContainer
                  position="top-right"
                  autoClose={3000}
                  hideProgressBar={false}
                  newestOnTop
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="light"
                />
                {/* App-wide real-time notification toasts (new booking, pending approval, …). */}
                <GlobalNotifications />
                <ErrorBoundary>
                  <GodModePanel />
                </ErrorBoundary>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  <Route path="/quick-onboarding" element={<QuickOnboarding />} />
                  <Route path="/instagram-onboarding" element={<InstagramShowcaseFlow />} />
                  <Route path="/oauth-redirect" element={<OAuthRedirectPage />} />
                  <Route path="/oauth-callback" element={<OAuthCallbackHandler />} />
                  <Route path="/stylist-signup-page" element={<StylistSignupPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/tnc" element={<TNC />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/instagram-data-deletion" element={<InstagramDataDeletionPage />} />
                  <Route path="/email-success" element={<EmailVerificationSuccess />} />
                  <Route path="/quick-email-verification-success" element={<QuickEmailVerificationSuccess />} />
                  <Route path="/login" element={<HomePage />} />
                  <Route element={<ErrorBoundary fallback={<ErrorFallback />}><Layout /></ErrorBoundary>}>
                    {/* --- Revamped (new design) pages served at the primary menu paths --- */}
                    <Route path="/dashboard" element={<ErrorBoundary fallback={<ErrorFallback />}><DashboardNew /></ErrorBoundary>} />
                    <Route path="/stylist" element={<ErrorBoundary fallback={<ErrorFallback />}><StylistPageNew /></ErrorBoundary>} />
                    <Route path="/stylist-profile" element={<ErrorBoundary fallback={<ErrorFallback />}><StylistProfileNew /></ErrorBoundary>} />
                    <Route path="/reservations/:stylistId/:filter?" element={<ErrorBoundary fallback={<ErrorFallback />}><ReservationsPageV2 /></ErrorBoundary>} />
                    <Route path="/reservations/:stylistId" element={<ErrorBoundary fallback={<ErrorFallback />}><ReservationsPageV2 /></ErrorBoundary>} />
                    <Route path="/reservations" element={<ErrorBoundary fallback={<ErrorFallback />}><ReservationsPageV2 /></ErrorBoundary>} />
                    <Route path='/credit-logs' element={<ErrorBoundary fallback={<ErrorFallback />}><CreditLogPageNew /></ErrorBoundary>} />
                    <Route path="/business-profile" element={<ErrorBoundary fallback={<ErrorFallback />}><BusinessProfileNew /></ErrorBoundary>} />
                    <Route path="/plans" element={<ErrorBoundary fallback={<ErrorFallback />}><PlanPageNew /></ErrorBoundary>} />
                    <Route path="/showcase-management/:stylistId?" element={<ErrorBoundary fallback={<ErrorFallback />}><ShowcaseManagementPageNew /></ErrorBoundary>} />
                    <Route path="/transactions" element={<ErrorBoundary fallback={<ErrorFallback />}><TransactionPage /></ErrorBoundary>} />
                    <Route path="/payout-logs" element={<ErrorBoundary fallback={<ErrorFallback />}><PayoutLogPage /></ErrorBoundary>} />
                    <Route path="/chat-list" element={<ErrorBoundary fallback={<ErrorFallback />}><ChatListNew /></ErrorBoundary>} />
                    <Route path="/chat/:userId" element={<ErrorBoundary fallback={<ErrorFallback />}><ConversationPage /></ErrorBoundary>} />
                    <Route path="/notification/:type" element={<ErrorBoundary fallback={<ErrorFallback />}><NotificationPageNew /></ErrorBoundary>} />
                    <Route path="/monthly-report" element={<ErrorBoundary fallback={<ErrorFallback />}><MonthlyReportNew /></ErrorBoundary>} />
                    <Route path="/instagram-auth-success" element={<ErrorBoundary fallback={<ErrorFallback />}><InstagramAuthSuccess /></ErrorBoundary>} />
                    <Route path="/reviews" element={<ErrorBoundary fallback={<ErrorFallback />}><ReviewsPageNew /></ErrorBoundary>} />

                    {/* --- Classic (original) versions, reachable via the 'Classic view' toggle --- */}
                    <Route path="/classic/dashboard" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/dashboard"><Dashboard /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/stylist" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/stylist"><StylistPage /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/reservations/:stylistId/:filter?" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/reservations"><ReservationsPageNew /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/reservations/:stylistId" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/reservations"><ReservationsPageNew /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/reservations" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/reservations"><ReservationsPageNew /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/credit-logs" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/credit-logs"><CreditLogPage /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/business-profile" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/business-profile"><BusinessProfile /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/plans" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/plans"><PlanPage /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/showcase-management" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/showcase-management"><ShowcaseManagementPage /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/chat-list" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/chat-list"><ChatList /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/monthly-report" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/monthly-report"><MonthlyReport /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/reviews" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/reviews"><ReviewsPage /></ClassicWrapper></ErrorBoundary>} />
                    <Route path="/classic/stylist-profile" element={<ErrorBoundary fallback={<ErrorFallback />}><ClassicWrapper newPath="/stylist-profile"><StylistProfileSettings /></ClassicWrapper></ErrorBoundary>} />
                  </Route>
                </Routes>
              </Router>
            </GodModeProvider>
          </CountProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;

